import type { ServiceInput } from '@seek/indie-api-types';
import type { FilterData } from './types.ts';
import type { useQuery } from '@tanstack/react-query';

const isSameFilters = ({
  fromState,
  fromQuery,
}: {
  fromState: FilterData[];
  fromQuery: ServiceInput.IntegrationStatistics['filters'];
}) => {
  if (fromState.length !== fromQuery.length) {
    return false;
  }

  return fromState.every((f, i) => {
    const q = fromQuery[i];
    return (
      f.type === q.type && f.comparator === q.comparator && f.value === q.value
    );
  });
};

// I'm sorry.
// Goal is to not show a loading state if the time auto-updates
export const shouldConsiderAsLoading = <
  T extends {
    parameters:
      ServiceInput.IntegrationStatistics | ServiceInput.IntegrationRequests;
  } | null,
>({
  query,
  filters,
  groupBy,
  bounds,
}: {
  query: Pick<ReturnType<typeof useQuery<T>>, 'status' | 'data'>;
  filters: FilterData[];
  groupBy?: string;
  bounds: [Date, Date];
}) => {
  if (query.status !== 'success' || !query.data) {
    return true;
  }

  if (
    !isSameFilters({
      fromState: filters,
      fromQuery: query.data.parameters.filters,
    })
  ) {
    return true;
  }

  if (
    groupBy !==
    ('groupBy' in query.data.parameters
      ? query.data.parameters.groupBy
      : undefined)
  ) {
    return true;
  }

  const moveAmountStart =
    bounds[0].getTime() -
    new Date(query.data.parameters.timeRange.start).getTime();

  const moveAmountEnd =
    bounds[1].getTime() -
    new Date(query.data.parameters.timeRange.end).getTime();

  const didMoveSlightlyForward =
    moveAmountStart === moveAmountEnd &&
    moveAmountStart > 0 &&
    // We refresh every minute, but this may be delayed with inactive tabs and such
    moveAmountStart < 60_000 * 2.2;

  return !didMoveSlightlyForward;
};
